import { useState } from "react";
import { useDispatch } from "react-redux";
import { setNotification } from "features/Notification/notificationSlice";
import authHeader from "services/auth-header";
import API from "services/axios";

/**
 * Downloads the whole work order as a zip:
 *   workorder.json  + assets/{products,lot-images,numbering-sheet,files}
 */
export default function ExportWorkOrderZip({
  selectedWO,
  btnClasses,
  onLoadingChange,
}) {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const setLoadingState = (val) => {
    setLoading(val);
    onLoadingChange?.(val);
  };

  // With responseType "blob" an error body also arrives as a Blob, so the
  // server's message is invisible unless we read the blob back as text.
  const readErrorMessage = async (err) => {
    const data = err?.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        if (parsed?.message) return parsed.message;
      } catch {
        // not JSON -- fall through to the generic message
      }
    }
    return data?.message || err?.message || "Failed to export work order.";
  };

  const handleExport = async () => {
    if (!selectedWO?.id) return;
    try {
      setLoadingState(true);
      const response = await API.get(`/work-orders/export-zip/${selectedWO.id}`, {
        headers: authHeader(),
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `WO-${selectedWO.woId}-export.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      dispatch(
        setNotification({
          message: await readErrorMessage(err),
          type: "error",
        }),
      );
    } finally {
      setLoadingState(false);
    }
  };

  return (
    <button
      type="button"
      disabled={loading}
      onClick={handleExport}
      className={btnClasses || "btn w-100 text-start text-nowrap"}
    >
      {loading ? (
        <>
          <span
            className="spinner-border spinner-border-sm me-1"
            role="status"
          />
          Exporting...
        </>
      ) : (
        "Export Work Order (Zip)"
      )}
    </button>
  );
}
